import { useState, useEffect, useCallback } from 'react';
import { 
  isStandalone, 
  isPWAMode, 
  isBrowserMode, 
  getDisplayMode,
  isPWAInstallSupported,
  isIOS, 
  isAndroid, 
  isMobileDevice,
  isIncognito
} from '../utils/deviceDetect';

declare global {
  interface BeforeInstallPromptEvent extends Event {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
  }
  interface Window {
    deferredPrompt: BeforeInstallPromptEvent | null;
  }
}

export function usePWAInstall() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOSDevice] = useState(isIOS);
  const [isAndroidDevice] = useState(isAndroid);
  const [isMobile] = useState(isMobileDevice);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [installDismissed, setInstallDismissed] = useState(false);
  const [displayMode, setDisplayMode] = useState<string>('browser');
  const [installSupported, setInstallSupported] = useState(false);
  const [isIncognitoMode, setIsIncognitoMode] = useState(false);

  // Check if the app is already installed and update status
  useEffect(() => {
    const checkInstallationStatus = async () => {
      const installed = isStandalone();
      const currentDisplayMode = getDisplayMode();
      const supported = isPWAInstallSupported();
      
      // Check for incognito mode using the improved detection
      const incognito = await isIncognito();
      
      setIsInstalled(installed);
      setDisplayMode(currentDisplayMode);
      setInstallSupported(supported && !incognito);
      setIsIncognitoMode(incognito);
      
      console.log('PWA Status Check:', {
        installed,
        displayMode: currentDisplayMode,
        isPWAMode: isPWAMode(),
        isBrowserMode: isBrowserMode(),
        installSupported: supported && !incognito,
        isMobile: isMobile,
        isIncognitoMode: incognito
      });
      
      // If not installed and not dismissed, show banner after delay
      if (!installed && !installDismissed && isMobile && !incognito) {
        const timer = setTimeout(() => {
          setShowInstallBanner(true);
        }, 3000); // Show after 3 seconds
        
        return () => clearTimeout(timer);
      }
    };

    checkInstallationStatus();
  }, [installDismissed, isMobile]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('BeforeInstallPrompt event fired', e);
      
      // Prevent the default prompt
      e.preventDefault();
      
      // Save the event for later use
      const promptEvent = e as BeforeInstallPromptEvent;
      setInstallPrompt(promptEvent);
      setCanInstall(true);
      
      // Store the prompt globally for debugging
      (window as any).deferredPrompt = promptEvent;
      
      // Show our custom banner instead of browser's default
      if (!installDismissed) {
        setShowInstallBanner(true);
      }
      
      console.log('Install prompt available, canInstall set to true');
    };

    const handleAppInstalled = () => {
      console.log('App was installed');
      setIsInstalled(true);
      setCanInstall(false);
      setInstallPrompt(null);
      setShowInstallBanner(false);
    };

    // Check if we already have a deferred prompt
    const checkExistingPrompt = () => {
      if ((window as any).deferredPrompt) {
        console.log('Found existing deferred prompt');
        setInstallPrompt((window as any).deferredPrompt);
        setCanInstall(true);
      }
    };

    // Try to trigger the install prompt immediately if conditions are met
    const tryTriggerInstallPrompt = () => {
      // Only try if we're in browser mode and not already installed
      if (!isInstalled && isBrowserMode() && 'serviceWorker' in navigator) {
        console.log('Attempting to trigger install prompt on page load');
        
        // Check for existing prompt first
        checkExistingPrompt();
        
        // Try to trigger the beforeinstallprompt event
        const event = new Event('beforeinstallprompt');
        window.dispatchEvent(event);
      }
    };

    // Check for existing prompt immediately
    checkExistingPrompt();

    // Try to trigger after a short delay to ensure page is loaded
    const timer = setTimeout(tryTriggerInstallPrompt, 1000);

    // Only add event listeners if the app is not already installed
    if (!isInstalled) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.addEventListener('appinstalled', handleAppInstalled);
    }

    return () => {
      clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled, installDismissed]);

  const handleInstallClick = useCallback(async (): Promise<boolean> => {
    console.log('Install click triggered', { 
      isIOSDevice, 
      installPrompt: !!installPrompt, 
      canInstall,
      deferredPrompt: !!(window as any).deferredPrompt,
      isInstalled,
      isPWAMode: isPWAMode(),
      isBrowserMode: isBrowserMode()
    });

    // Check if already installed
    if (isInstalled || isPWAMode()) {
      console.log('App is already installed');
      return true;
    }

    // Try to use the stored prompt first
    let promptToUse = installPrompt || (window as any).deferredPrompt;
    
    if (promptToUse) {
      try {
        console.log('Showing native install prompt');
        await promptToUse.prompt();
        const { outcome } = await promptToUse.userChoice;
        
        console.log('Install prompt outcome:', outcome);
        
        if (outcome === 'accepted') {
          console.log('User accepted the install prompt');
          setIsInstalled(true);
          setCanInstall(false);
          setInstallPrompt(null);
          setShowInstallBanner(false);
          // Clear the global prompt
          (window as any).deferredPrompt = null;
          return true;
        } else {
          console.log('User dismissed the install prompt');
          return false;
        }
      } catch (error) {
        console.error('Error showing install prompt:', error);
        // Fall through to alternative methods
      }
    }

    // For iOS Safari, show instructions instead of trying to trigger prompt
    if (isIOSDevice) {
      console.log('iOS device detected, showing instructions');
      return false; // This will trigger showing instructions
    }

    // If no native prompt available, try alternative methods
    console.log('No native install prompt available, trying alternative methods');
    
    // For iOS devices, try to trigger the install prompt first
    if (isIOSDevice) {
      console.log('iOS device detected, trying to trigger install prompt');
      
      // Try to trigger the beforeinstallprompt event for iOS
      try {
        const event = new Event('beforeinstallprompt');
        window.dispatchEvent(event);
        
        // Wait a bit to see if the event was handled
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Check if we now have a prompt
        const newPrompt = (window as any).deferredPrompt || installPrompt;
        if (newPrompt) {
          console.log('iOS install prompt triggered successfully');
          await newPrompt.prompt();
          const { outcome } = await newPrompt.userChoice;
          
          if (outcome === 'accepted') {
            setIsInstalled(true);
            setCanInstall(false);
            setInstallPrompt(null);
            setShowInstallBanner(false);
            (window as any).deferredPrompt = null;
            return true;
          }
        }
      } catch (error) {
        console.log('iOS install prompt failed:', error);
      }
      
      // If iOS prompt failed, show instructions
      console.log('iOS install prompt not available, showing instructions');
      return false;
    }
    
    // Try to trigger the install prompt by creating a temporary element and clicking it
    try {
      // Create a temporary link element that might trigger install
      const tempLink = document.createElement('a');
      tempLink.href = window.location.href;
      tempLink.style.display = 'none';
      document.body.appendChild(tempLink);
      
      // Try different approaches to trigger install
      const approaches = [
        // Approach 1: Simulate beforeinstallprompt event
        () => {
          console.log('Trying to simulate beforeinstallprompt event');
          const event = new Event('beforeinstallprompt');
          window.dispatchEvent(event);
        },
        // Approach 2: Try to access the install prompt through the browser's internal APIs
        () => {
          console.log('Trying service worker approach');
          if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({ type: 'INSTALL_PROMPT' });
          }
        },
        // Approach 3: Try to trigger through user gesture simulation
        () => {
          console.log('Trying user gesture simulation');
          tempLink.click();
        },
        // Approach 4: Try to trigger through touch events (mobile specific)
        () => {
          console.log('Trying touch event simulation');
          const touchEvent = new TouchEvent('touchstart', {
            bubbles: true,
            cancelable: true,
            touches: [new Touch({
              identifier: 0,
              target: tempLink,
              clientX: 0,
              clientY: 0,
              screenX: 0,
              screenY: 0,
              pageX: 0,
              pageY: 0
            })]
          });
          tempLink.dispatchEvent(touchEvent);
        }
      ];
      
      for (const approach of approaches) {
        try {
          approach();
          await new Promise(resolve => setTimeout(resolve, 1000)); // Increased delay for mobile
          
          // Check if we now have a prompt
          const newPrompt = (window as any).deferredPrompt || installPrompt;
          if (newPrompt) {
            console.log('Alternative method triggered install prompt');
            await newPrompt.prompt();
            const { outcome } = await newPrompt.userChoice;
            
            if (outcome === 'accepted') {
              setIsInstalled(true);
              setCanInstall(false);
              setInstallPrompt(null);
              setShowInstallBanner(false);
              (window as any).deferredPrompt = null;
              document.body.removeChild(tempLink);
              return true;
            }
          }
        } catch (approachError) {
          console.log('Approach failed:', approachError);
        }
      }
      
      // Clean up
      if (document.body.contains(tempLink)) {
        document.body.removeChild(tempLink);
      }
    } catch (error) {
      console.error('Error with alternative install methods:', error);
    }

    // If all else fails, return false to show instructions
    console.log('All install methods failed, will show instructions');
    return false;
  }, [installPrompt, isIOSDevice, isAndroidDevice, canInstall, isInstalled]);

  const dismissInstallBanner = useCallback(() => {
    setShowInstallBanner(false);
    setInstallDismissed(true);
    // Store dismissal in localStorage to remember user's choice
    localStorage.setItem('pwa-install-dismissed', 'true');
  }, []);

  const resetInstallDismissal = useCallback(() => {
    setInstallDismissed(false);
    localStorage.removeItem('pwa-install-dismissed');
  }, []);

  const triggerInstallPrompt = useCallback(() => {
    console.log('Manually triggering install prompt');
    
    // Try to trigger the beforeinstallprompt event
    const event = new Event('beforeinstallprompt');
    window.dispatchEvent(event);
    
    // Also try to access any existing deferred prompt
    if ((window as any).deferredPrompt) {
      console.log('Found existing deferred prompt, setting canInstall to true');
      setInstallPrompt((window as any).deferredPrompt);
      setCanInstall(true);
    }
  }, []);

  const triggerMobileInstall = useCallback(async () => {
    console.log('Triggering mobile-specific install methods');
    
    // For mobile devices, try different approaches
    if (isMobile) {
      try {
        // Method 1: Try to trigger the install prompt through user interaction
        const installButton = document.createElement('button');
        installButton.style.position = 'fixed';
        installButton.style.top = '-1000px';
        installButton.style.left = '-1000px';
        installButton.style.width = '1px';
        installButton.style.height = '1px';
        installButton.style.opacity = '0';
        installButton.textContent = 'Install App';
        document.body.appendChild(installButton);
        
        // Simulate user interaction
        installButton.click();
        
        // Wait a bit
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Check if we have a prompt now
        if ((window as any).deferredPrompt) {
          console.log('Mobile install prompt triggered');
          setInstallPrompt((window as any).deferredPrompt);
          setCanInstall(true);
        }
        
        // Clean up
        document.body.removeChild(installButton);
        
      } catch (error) {
        console.error('Mobile install trigger failed:', error);
      }
    }
  }, [isMobile]);

  // Check if user previously dismissed the install banner
  useEffect(() => {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed === 'true') {
      setInstallDismissed(true);
    }
  }, []);

  return {
    installPrompt,
    canInstall,
    isInstalled,
    isIOSDevice,
    isAndroidDevice,
    isMobile,
    showInstallBanner,
    installDismissed,
    displayMode,
    installSupported,
    isIncognito: isIncognitoMode,
    isPWAMode: isPWAMode(),
    isBrowserMode: isBrowserMode(),
    handleInstallClick,
    dismissInstallBanner,
    resetInstallDismissal,
    triggerInstallPrompt,
    triggerMobileInstall,
  };
}
